/**
 * The shift and the drawer, said in a few words for the line under the site
 * name and the rows of the tablet menu. Hooks only, so the sheets that act on
 * them can live in their own files.
 */

import { useEffect, useState } from 'react';
import { useCurrentDrawer, useMyShift, usePermission } from '@restaurant/api-client';

import { tillSettled, useThisTill } from './this-till';

export type Status = { label: string; tone: 'ok' | 'warn' | 'neutral' };

/**
 * Whether this terminal's user is on the clock, said in a few words for the
 * line under the site name. Null when there is nothing to clock: an owner who
 * never put themselves on the payroll should not be told to clock in.
 */
export function useClockStatus(): Status | null {
  const shift = useMyShift();
  const now = useTicking(shift.data?.on_shift ?? false);

  if (shift.data && shift.data.employee_id === null) return null;
  if (!shift.data) return null;
  if (shift.data.on_break) return { label: 'On a break', tone: 'warn' };
  if (!shift.data.on_shift) return { label: 'Not clocked in', tone: 'warn' };
  // Nobody works sixteen hours: somebody forgot to clock out, and "311h 29m"
  // read as a number rather than as a problem.
  if (forgotten(shift.data.started_at, now))
    return { label: 'Still clocked in?', tone: 'warn' };
  return { label: `On shift ${elapsed(shift.data.started_at, now)}`, tone: 'ok' };
}

export function elapsed(iso: string | null, now: number): string {
  if (iso === null) return '';
  const minutes = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function forgotten(iso: string | null, now: number): boolean {
  return iso !== null && now - Date.parse(iso) > 16 * 60 * 60_000;
}

/**
 * A clock that moves, but only while there is something to count.
 *
 * The elapsed time is derived from a timestamp that does not change, so
 * nothing would redraw between one poll and the next — a chip reading "3h 12m"
 * would still say it an hour later. Stopped entirely when off the clock,
 * because a timer running on a terminal nobody is using is a wakeup a minute
 * for no reason.
 */
export function useTicking(running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [running]);
  return now;
}

/**
 * Whether this till can take cash, in a few words for the line under the site
 * name. Null for somebody who does not take payments.
 */
export function useDrawerStatus(
  locationId: string | undefined,
): { label: string; tone: 'ok' | 'warn' | 'neutral' } | null {
  const mayTake = usePermission('payments.payment.take');
  const { state, terminalId } = useThisTill(locationId);
  const drawer = useCurrentDrawer(tillSettled(state) ? locationId : undefined, terminalId);

  if (!mayTake) return null;
  if (state.kind === 'loading' || (tillSettled(state) && drawer.isPending)) return null;
  if (state.kind === 'unchosen') return { label: 'Choose this till', tone: 'warn' };
  return drawer.data
    ? { label: 'Drawer open', tone: 'ok' }
    : { label: 'Drawer not open', tone: 'warn' };
}

/** Floats a till usually starts with, one tap each. */
export const FLOATS = ['0', '50', '100', '150', '200'] as const;

// --------------------------------------------------------------------------- //
// Before the first sale (`shift-start.tsx`)
// --------------------------------------------------------------------------- //

type StepState = 'todo' | 'done' | 'skip';

export interface ShiftSteps {
  clock: StepState;
  till: StepState;
  drawer: StepState;
  /** Steps still to do, in order. */
  left: number;
  /** Whether the answer is known yet: nothing is offered while it is not. */
  ready: boolean;
}

/** What is left before this till can take its first sale. */
export function useShiftSteps(locationId: string | undefined): ShiftSteps {
  const shift = useMyShift();
  const till = useThisTill(locationId);
  const mayTake = usePermission('payments.payment.take');
  const drawer = useCurrentDrawer(
    tillSettled(till.state) && mayTake ? locationId : undefined,
    till.terminalId,
  );

  const clock: StepState =
    !shift.data || shift.data.employee_id === null
      ? 'skip'
      : shift.data.on_shift
        ? 'done'
        : 'todo';
  const tillStep: StepState =
    till.state.kind === 'none-registered' || till.state.kind === 'loading'
      ? 'skip'
      : till.state.kind === 'unchosen'
        ? 'todo'
        : 'done';
  // The drawer waits on the till: which drawer it is depends on which till.
  const drawerStep: StepState = !mayTake
    ? 'skip'
    : tillStep === 'todo' || !drawer.data
      ? 'todo'
      : 'done';

  const ready =
    !shift.isPending &&
    till.state.kind !== 'loading' &&
    (!mayTake || !tillSettled(till.state) || !drawer.isPending);
  const left = [clock, tillStep, drawerStep].filter((s) => s === 'todo').length;
  return { clock, till: tillStep, drawer: drawerStep, left, ready };
}
